/**
 * @fileoverview Componente principal de la aplicación Nubelytics
 * 
 * Responsable de la configuración del router, providers de contexto,
 * inicialización de Nexo, integración del sistema CORS avanzado
 * y renderizado de componentes principales.
 * Sigue principios SOLID con responsabilidades bien definidas.
 */

import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DiagnosticProvider } from './context/DiagnosticContext';
import { StoreProvider } from './contexts/StoreContext';
import { QueryClientProvider } from '@tanstack/react-query';
import queryClient from './queryClient';
import nexoClient from './nexoClient';
import { connect, iAmReady } from '@tiendanube/nexo';
import DiagnosticsPanel from './components/DiagnosticsPanel';

// Importamos el sistema CORS avanzado
import { AutoCorsProvider } from './infrastructure/cors/AutoCorsProvider';
import { integrateCorsSystem } from './infrastructure/cors/integrateCorsSystem';

// Solución para problemas de CSP con fuentes externas
// Al ser agregadas directamente, nos aseguramos de usar fuentes aprobadas por Tiendanube
const fontStyles = document.createElement('style');
fontStyles.textContent = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');
  body, button, input, textarea, select { 
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif !important; 
  }
`;
document.head.appendChild(fontStyles);

// Importamos la página de demostración
import DemoPage from './DemoPage';

// Páginas
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Orders from './pages/Orders';
// import Products from './pages/Products';
import ProductAnalytics from './pages/ProductAnalyticsNew';
import Stats from './pages/StatsNew';
import Carts from './pages/Carts';
import Alerts from './pages/Alerts';
import Settings from './pages/SettingsNew';
import Reports from './pages/ReportsNew';
import Billing from './pages/Billing';
import BillingProcessing from './pages/BillingProcessing';
import NotFound from './pages/NotFound';
import CustomerSegmentation from './pages/CustomerSegmentation';
import PredictiveAnalytics from './pages/PredictiveAnalytics';
import CompetitiveAnalysis from './pages/CompetitiveAnalysis';

// Componentes
import Layout from './components/Layout';
import DevDashboardDirect from './components/DevDashboardDirect';
import ThemeModeProvider from './context/ThemeModeContext';
import LoadingScreen from './components/LoadingScreen';
import TiendanubeRedirect from './components/TiendanubeRedirect';
import { NotificationProvider } from './components/common/NotificationSystem';

// Ruta protegida que requiere autenticación
const PrivateRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  return currentUser ? children : <Navigate to="/login" replace />;
};

// Inicializar sistema CORS avanzado
const corsSystem = integrateCorsSystem({
  enableTelemetry: true,
  withReactProvider: true,
  autoDetectProblems: true
});

function App() {
  // Esperar handshake con Admin
  const [nexoReady, setNexoReady] = useState(false);
  // Estado de inicialización CORS
  const [corsInitialized, setCorsInitialized] = useState(false);

  useEffect(() => {
    // Detectar si estamos embebidos en un iframe de Tiendanube
    const isEmbedded = window !== window.parent;
    
    if (isEmbedded) {
      console.log('[App] Detectada app embebida, iniciando conexión Nexo...');
      // Conectarse con el Admin y notificar readiness
      connect(nexoClient)
        .then(() => {
          console.log('[App] Conectado exitosamente con Nexo');
          setNexoReady(true);
          iAmReady(nexoClient);
        })
        .catch(err => {
          // Si hay un error en la conexión, permitimos continuar de todas formas
          // ya que el usuario podría estar usando la app fuera de Tiendanube
          console.error('[App] Error conectando Nexo, continuando sin integración:', err);
          setNexoReady(true); // Continuamos de todas formas
        });
    } else {
      // No estamos embebidos, no necesitamos Nexo
      console.log('[App] No estamos embebidos en iframe, saltando conexión Nexo');
      setNexoReady(true);
    }
  }, []);
  
  // Inicializar el sistema CORS avanzado una vez
  useEffect(() => {
    console.info('[App] Inicializando sistema CORS avanzado...');
    try {
      // Ejecutar diagnósticos iniciales y registrar interceptores
      corsSystem.corsRegistry.runDiagnosticTest()
        .then(results => {
          console.info('[App] Diagnóstico CORS completado:', 
            results.successCount + '/' + results.totalCount + ' endpoints OK');
        })
        .catch(err => console.warn('[App] Error en diagnóstico CORS:', err))
        .finally(() => setCorsInitialized(true));
    } catch (error) {
      console.error('[App] Error inicializando sistema CORS:', error);
      setCorsInitialized(true); // Continuamos de todas formas
    }
  }, []);

  // Mostrar pantalla de carga hasta que tanto Nexo como CORS estén listos
  if ((!nexoReady && window !== window.parent) || !corsInitialized) { 
    return <div style={{padding:'2rem',textAlign:'center'}}>Cargando aplicación...</div>;
  }

  return (
    <ThemeModeProvider>
      <QueryClientProvider client={queryClient}>
      <AutoCorsProvider>
      <AuthProvider>
        <StoreProvider>
        <DiagnosticProvider>
          <NotificationProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/auth/callback" element={<Login callback />} />
              <Route path="/api/auth/callback" element={<TiendanubeRedirect />} />

              {/* Ruta para la demostración - sin autenticación */}
              <Route path="/demo" element={<DemoPage />} />
              
              {/* Ruta de desarrollo - Dashboard sin autenticación */}
              {process.env.NODE_ENV === 'development' && (
                <Route path="/dev-dashboard" element={<Layout><DevDashboardDirect /></Layout>} />
              )}

              {/* Rutas protegidas dentro del layout principal */}
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <Layout />
                  </PrivateRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="orders" element={<Orders />} />
                <Route path="products" element={<ProductAnalytics />} />
                
                <Route path="stats" element={<Stats />} />
                <Route path="carts" element={<Carts />} />
                <Route path="/carts/reminders" element={<div>Recordatorios de Carritos</div>} />
                <Route path="/customers" element={<CustomerSegmentation />} />
                <Route path="/predictions" element={<PredictiveAnalytics />} />
                <Route path="/competitive" element={<CompetitiveAnalysis />} />
                <Route path="alerts" element={<Alerts />} />
                <Route path="settings" element={<Settings />} />
                <Route path="reports" element={<Reports />} />
                <Route path="configuracion" element={<Settings />} />
                <Route path="billing" element={<Billing />} />
                <Route path="billing/processing" element={<BillingProcessing />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>

            {/* Panel de diagnóstico flotante - Solo visible en desarrollo o con flag NUBELYTICS_DEBUG=true */}
            <DiagnosticsPanel />
          </Router>
          </NotificationProvider>
        </DiagnosticProvider>
        </StoreProvider>
      </AuthProvider>
    </AutoCorsProvider>
    </QueryClientProvider>
  </ThemeModeProvider>
  );
}

export default App;
